import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { getImageUrl, formatPrice } from '@/components/PropertyCard';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowRight, FileText, MapPin } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_LABELS = {
  tour: { en: 'Virtual Tour', es: 'Tour Virtual' },
  onboarding: { en: 'Documents', es: 'Documentos' },
  poa: { en: 'Power of Attorney', es: 'Poder Notarial' },
  due_diligence: { en: 'Due Diligence', es: 'Debida Diligencia' },
  offer: { en: 'Offer', es: 'Oferta' },
  contract: { en: 'Contract', es: 'Contrato' },
  payment: { en: 'Payment', es: 'Pago' },
  signing: { en: 'Signing', es: 'Firma' },
  registry: { en: 'Registry', es: 'Registro' },
  complete: { en: 'Complete', es: 'Completo' },
  delivery: { en: 'Delivery', es: 'Entrega' },
};

export default function TransactionsListPage() {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const tt = (en, es) => language === 'es' ? es : en;
  const STATUS_ES = {
    idle: 'Inactivo', requested: 'Solicitado', scheduled: 'Programado', completed: 'Completado', skipped: 'Omitido',
    profile_incomplete: 'Perfil Incompleto', profile_submitted: 'Perfil Enviado', profile_verified: 'Perfil Verificado',
    poa_missing: 'Poder Faltante', poa_uploaded: 'Poder Subido', poa_verified: 'Poder Verificado',
    pending: 'Pendiente', in_progress: 'En Progreso', failed: 'Fallido',
    not_started: 'No Iniciado', submitted: 'Enviada', accepted: 'Aceptada', rejected: 'Rechazada', counter_offer: 'Contra Oferta',
    draft_uploaded: 'Borrador Subido', under_review: 'En Revision', changes_requested: 'Cambios Solicitados', approved: 'Aprobado',
    proof_uploaded: 'Comprobante Subido', confirmed: 'Confirmado', flagged: 'Marcado',
    registered: 'Registrado', ownership_confirmed: 'Propiedad Confirmada',
    shipment_pending: 'Envio Pendiente', in_transit: 'En Transito', delivered: 'Entregado',
  };
  const ts = (status) => {
    if (!status) return '';
    if (language === 'es') return STATUS_ES[status] || status.replace(/_/g, ' ');
    return status.replace(/_/g, ' ');
  };

  useEffect(() => {
    axios.get(`${API}/transactions/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setTransactions(r.data.transactions))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-4">
      <Skeleton className="h-10 w-48" />
      {[1,2].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="transactions-list">
      <h1 className="font-heading text-2xl md:text-3xl font-semibold mb-6">{tt('My Transactions', 'Mis Transacciones')}</h1>

      {transactions.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-border">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">{tt('No transactions yet.', 'No hay transacciones aun.')}</p>
          <p className="text-sm text-slate-400 mt-1">{tt('Start a purchase from any property page.', 'Inicia una compra desde cualquier propiedad.')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {transactions.map(tx => {
            const prop = tx.property || {};
            const stageLabel = STAGE_LABELS[tx.current_stage]?.[language] || tx.current_stage;
            const stageStatus = tx.stages?.[tx.current_stage]?.status || '';
            return (
              <Link key={tx.id} to={`/transaction/${tx.id}`} data-testid={`tx-card-${tx.id}`}>
                <Card className="hover:shadow-md transition-shadow cursor-pointer mb-3">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-16 h-14 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                      <img src={getImageUrl(prop.main_photo)} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm truncate">{prop.title || 'Property'}</h3>
                      <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{prop.city}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-heading font-bold text-sm text-primary">{formatPrice(prop.price, prop.currency)}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <Badge variant="outline" className="text-[10px]">{stageLabel}</Badge>
                        <Badge className="text-[10px] bg-amber-100 text-amber-700 capitalize">{ts(stageStatus)}</Badge>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
